import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { authApi, formatApiError } from "../lib/api";
import { toast } from "sonner";

const MIN_LENGTH = 8;

const COPY = {
  en: {
    heading: "Set a new password",
    tagline: "Choose a password you do not use anywhere else.",
    password: "New password",
    confirm: "Confirm new password",
    submit: "Update password",
    saving: "Updating…",
    back: "Back to sign in",
    tooShort: `Password must be at least ${MIN_LENGTH} characters.`,
    mismatch: "The two passwords do not match.",
    noToken: "This link is missing its reset code.",
    noTokenBody: "Reset links expire after 30 minutes and work once. Ask for a new one.",
    askAgain: "Request a new link",
    done: "Password updated. You can sign in now.",
    show: "Show password",
    hide: "Hide password",
  },
  ms: {
    heading: "Tetapkan kata laluan baharu",
    tagline: "Pilih kata laluan yang anda tidak guna di tempat lain.",
    password: "Kata laluan baharu",
    confirm: "Sahkan kata laluan baharu",
    submit: "Kemas kini kata laluan",
    saving: "Mengemas kini…",
    back: "Kembali ke log masuk",
    tooShort: `Kata laluan mesti sekurang-kurangnya ${MIN_LENGTH} aksara.`,
    mismatch: "Kedua-dua kata laluan tidak sama.",
    noToken: "Pautan ini tiada kod set semula.",
    noTokenBody: "Pautan set semula luput selepas 30 minit dan sah sekali sahaja. Minta yang baharu.",
    askAgain: "Minta pautan baharu",
    done: "Kata laluan dikemas kini. Anda boleh log masuk sekarang.",
    show: "Tunjuk kata laluan",
    hide: "Sembunyi kata laluan",
  },
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lang] = useState(() => {
    try { return localStorage.getItem("bicarra.lang") === "ms" ? "ms" : "en"; }
    catch { return "en"; }
  });
  const t = COPY[lang];

  async function onSubmit(e) {
    e.preventDefault();
    if (loading) return;
    setError("");

    if (password.length < MIN_LENGTH) { setError(t.tooShort); return; }
    if (password !== confirm) { setError(t.mismatch); return; }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      toast.success(t.done);
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="b2 min-h-screen bg-b2-canvas text-b2-ink font-sans flex flex-col">
      <header className="px-6 h-[72px] flex items-center">
        <Link to="/" className="flex items-center gap-2">
          <img src="/brand/bicarra-icon.svg" alt="" className="w-[26px] h-[26px] rounded-[7px]" />
          <span className="text-[19px] font-bold tracking-[-0.03em] text-b2-ink lowercase">bicarra</span>
        </Link>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 pt-10 pb-20">
        <div className="w-full max-w-[400px]">
          {!token ? (
            <div data-testid="reset-no-token">
              <h1 className="text-[28px] leading-[1.2] font-bold tracking-[-0.03em]">{t.noToken}</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-b2-inksoft">{t.noTokenBody}</p>
              <Link
                to="/forgot-password"
                className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-[10px]
                           bg-b2-indigo hover:bg-b2-indigohover text-white text-[15px] font-semibold transition-colors"
              >
                {t.askAgain}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-[28px] leading-[1.2] font-bold tracking-[-0.03em]">{t.heading}</h1>
              <p className="mt-3 text-[15px] leading-relaxed text-b2-inksoft">{t.tagline}</p>

              <form onSubmit={onSubmit} className="mt-8 space-y-4">
                <div>
                  <label htmlFor="rp-password" className="block text-[13.5px] font-medium text-b2-muted mb-1.5">
                    {t.password}
                  </label>
                  <div className="relative">
                    <input
                      id="rp-password" type={show ? "text" : "password"} required autoFocus
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      data-testid="reset-password"
                      className="w-full h-12 pl-4 pr-11 rounded-[10px] bg-white border border-b2-line
                                 text-[15px] text-b2-ink focus:outline-none focus:border-b2-indigo transition-colors"
                    />
                    <button
                      type="button" onClick={() => setShow((v) => !v)}
                      aria-label={show ? t.hide : t.show}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-b2-faint hover:text-b2-muted"
                    >
                      {show ? <EyeOff className="w-[18px] h-[18px]" strokeWidth={1.8} />
                            : <Eye className="w-[18px] h-[18px]" strokeWidth={1.8} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="rp-confirm" className="block text-[13.5px] font-medium text-b2-muted mb-1.5">
                    {t.confirm}
                  </label>
                  <input
                    id="rp-confirm" type={show ? "text" : "password"} required
                    value={confirm} onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-confirm"
                    className="w-full h-12 px-4 rounded-[10px] bg-white border border-b2-line
                               text-[15px] text-b2-ink focus:outline-none focus:border-b2-indigo transition-colors"
                  />
                </div>

                {error && (
                  <p data-testid="reset-error"
                     className="text-[13.5px] text-b2-attention bg-b2-attentionwash rounded-[10px] px-3.5 py-2.5">
                    {error}
                  </p>
                )}

                <button
                  type="submit" disabled={loading} data-testid="reset-submit"
                  className="w-full h-12 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover
                             disabled:opacity-60 text-white text-[15px] font-semibold
                             inline-flex items-center justify-center gap-2 transition-colors"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {loading ? t.saving : t.submit}
                </button>
              </form>
            </>
          )}

          <p className="mt-8 text-[14px] text-b2-inksoft">
            <Link to="/login" className="text-b2-indigo hover:underline">{t.back}</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
